var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { serializer, transformError } from '@wix/metro-runtime/velo';
import * as ambassadorWixTableReservationsV1TimeSlot from '@wix/ambassador-table-reservations-v1-time-slot/http';
const _toVeloEntity = '$';
const _fromVeloEntity = '$';
export var Status;
(function (Status) {
    Status["AVAILABLE"] = "AVAILABLE";
    Status["UNAVAILABLE"] = "UNAVAILABLE";
    Status["NON_WORKING_HOURS"] = "NON_WORKING_HOURS";
})(Status || (Status = {}));
export var TableConflictType;
(function (TableConflictType) {
    TableConflictType["UNKNOWN"] = "UNKNOWN";
    TableConflictType["TABLE_RESERVED"] = "TABLE_RESERVED";
    TableConflictType["TABLE_TOO_BIG"] = "TABLE_TOO_BIG";
    TableConflictType["TABLE_TOO_SMALL"] = "TABLE_TOO_SMALL";
    TableConflictType["OFFLINE_ONLY"] = "OFFLINE_ONLY";
})(TableConflictType || (TableConflictType = {}));
export var TableCombinationConflictType;
(function (TableCombinationConflictType) {
    TableCombinationConflictType["UNKNOWN"] = "UNKNOWN";
    TableCombinationConflictType["RESERVED"] = "RESERVED";
    TableCombinationConflictType["TOO_BIG"] = "TOO_BIG";
    TableCombinationConflictType["TOO_SMALL"] = "TOO_SMALL";
    TableCombinationConflictType["OFFLINE_ONLY"] = "OFFLINE_ONLY";
})(TableCombinationConflictType || (TableCombinationConflictType = {}));
export var Type;
(function (Type) {
    Type["UNKNOWN"] = "UNKNOWN";
    Type["PARTY_PACING"] = "PARTY_PACING";
    Type["SEAT_PACING"] = "SEAT_PACING";
})(Type || (Type = {}));
const _checkReservationDetailsRequest = {};
const _checkReservationDetailsResponse = {};
const _getTimeSlotsRequest = {};
const _getTimeSlotsResponse = {};
/**
 * Returns a list of time slots at a given restaurant on a given `date`, and their availability for a given `partySize`.
 *
 * Without passing optional parameters, the list will contain a single time slot at the given `date`.
 * Use `slotsBefore` and `slotsAfter` to get additional time slots before and after the given `date`.
 *
 * If you do not provide a `duration`, the duration will be calculated automatically based on the reservation location's configuration.
 * The reservation location's settings used to determine the duration are its `defaultTurnoverTime` and `turnoverTimeRules`. These specify how much time should be allotted for a reservation of a party of a given size.
 *
 * The `startDate`s of time slots in the response are 15 minutes apart regardless of the `duration` provided.
 * @param reservationLocationId - ID of the reservation location for which to retrieve time slots.
 * @param date - Date and time for which to retrieve a time slot.
 * @param partySize - Size of the party that needs to be seated during this time slot.
 *
 * Min: `1`
 * @public
 * @documentationMaturity preview
 * @requiredField date
 * @requiredField partySize
 * @requiredField reservationLocationId
 * @param options - Options for retrieving the time slots.
 */
export function getTimeSlots(reservationLocationId, date, partySize, options) {
    var _a, _b, _c;
    return __awaiter(this, arguments, void 0, function* () {
        const requestTransformation = {
            reservationLocationId: '$[0]',
            date: '$[1]',
            partySize: '$[2]',
            duration: '$[3].duration',
            slotsBefore: '$[3].slotsBefore',
            slotsAfter: '$[3].slotsAfter',
        };
        const responseTransformation = '$';
        // @ts-ignore
        const { httpClient, sideEffects, additionalHeaders } = arguments[4];
        const { toAmbassadorRequest } = serializer({
            rootSchema: _getTimeSlotsRequest,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _fromVeloEntity,
            },
            customTransformation: requestTransformation,
        });
        const { fromJSON } = serializer({
            rootSchema: _getTimeSlotsResponse,
            depSchemas: {},
            fqdnTransformation: {
                paths: [...['Array#timeSlots']],
                transformation: _toVeloEntity,
            },
            customTransformation: responseTransformation,
        });
        const payload = toAmbassadorRequest([
            reservationLocationId,
            date,
            partySize,
            options,
        ]);
        const reqOpts = ambassadorWixTableReservationsV1TimeSlot.getTimeSlots(payload);
        const reqOptionWithHeadersWrapper = (args) => {
            const result = reqOpts(args);
            if (additionalHeaders) {
                const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                return Object.assign(Object.assign({}, result), { headers });
            }
            return result;
        };
        (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
        try {
            const result = yield httpClient.request(reqOptionWithHeadersWrapper);
            (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
            return fromJSON(result.data);
        }
        catch (err) {
            const transformedError = transformError(err, requestTransformation, [
                'reservationLocationId',
                'date',
                'partySize',
                'options',
            ]);
            (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
            throw transformedError;
        }
    });
}
/**
 * Check reservation details.
 * @param reservationLocationId - Reservation location ID.
 * @internal
 * @documentationMaturity preview
 * @requiredField options.date
 * @requiredField options.duration
 * @requiredField options.partySize
 * @requiredField reservationLocationId
 * @adminMethod
 */
export function checkReservationDetails(reservationLocationId, options) {
    var _a, _b, _c;
    return __awaiter(this, arguments, void 0, function* () {
        const requestTransformation = {
            reservationLocationId: '$[0]',
            date: '$[1].date',
            duration: '$[1].duration',
            partySize: '$[1].partySize',
            excludeReservationId: '$[1].excludeReservationId',
            tableIds: '$[1].tableIds',
        };
        const responseTransformation = '$';
        // @ts-ignore
        const { httpClient, sideEffects, additionalHeaders } = arguments[2];
        const { toAmbassadorRequest } = serializer({
            rootSchema: _checkReservationDetailsRequest,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _fromVeloEntity,
            },
            customTransformation: requestTransformation,
        });
        const { fromJSON } = serializer({
            rootSchema: _checkReservationDetailsResponse,
            depSchemas: {},
            fqdnTransformation: {
                paths: [],
                transformation: _toVeloEntity,
            },
            customTransformation: responseTransformation,
        });
        const payload = toAmbassadorRequest([reservationLocationId, options]);
        const reqOpts = ambassadorWixTableReservationsV1TimeSlot.checkReservationDetails(payload);
        const reqOptionWithHeadersWrapper = (args) => {
            const result = reqOpts(args);
            if (additionalHeaders) {
                const headers = Object.assign(Object.assign({}, (result.hasOwnProperty('headers') ? result.headers : {})), additionalHeaders);
                return Object.assign(Object.assign({}, result), { headers });
            }
            return result;
        };
        (_a = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSiteCall) === null || _a === void 0 ? void 0 : _a.call(sideEffects);
        try {
            const result = yield httpClient.request(reqOptionWithHeadersWrapper);
            (_b = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onSuccess) === null || _b === void 0 ? void 0 : _b.call(sideEffects, result);
            return fromJSON(result.data);
        }
        catch (err) {
            const transformedError = transformError(err, requestTransformation, [
                'reservationLocationId',
                'options',
            ]);
            (_c = sideEffects === null || sideEffects === void 0 ? void 0 : sideEffects.onError) === null || _c === void 0 ? void 0 : _c.call(sideEffects, err);
            throw transformedError;
        }
    });
}
//# sourceMappingURL=table-reservations-v1-time-slot.universal.js.map